/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { isObjEmptyOrNull, isStrEmptyOrNull } from '../../common/UtilityFunctions';
import {
  earliestPossibleEffectiveDate,
  getEffectiveDateAsDatabaseStr,
  useEscrowAnalysisCalculationContext,
} from '../../contexts/CalculateEscrowAnalysisContext';
import { Autocomplete, Box, Button, TextField } from '@mui/material';
import { useMemo } from 'react';

import { DateField, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Styles from '../../css/ServicerEscrowAnalysis.module.css';
import EscrowNumbersTable from '../tables/EscrowNumbers';
import EscrowMonthsTable from '../tables/EscrowMonthsTrialBalances';
import { useApplyAEAContext } from '../../contexts/ApplyAEAContext';
import LoadingModal from '../modals/LoadingModal';

const CalculateAEAView = () => {
  const {
    escrowCushionMonths,
    setEscrowCushionMonths,
    shortageMonths,
    setShortageMonths,
    effectiveDate,
    effectiveDateError,
    setEffectiveDate,
    setEffectiveDateError,
    escrowCalcErrors,
    escrowAnalysisCalculated,
    setEscrowAnalysisCalculated,
    handleCalculate,
    escrowAnalysisCalculation,
    escrowCalcLoading,
    effdatestr,
    getEscrowAnalysisNumbers,
    showEscrowNumbersTable,
    showEscrowMonthsTable,
    loadingDefaultValues,
  } = useEscrowAnalysisCalculationContext();

  const { applyAEAClicked, applyAEAResultRetrieved } = useApplyAEAContext();

  const { applyingAEA } = useApplyAEAContext();

  const escrowCalcErrorsText = () => {
    return !isStrEmptyOrNull(escrowCalcErrors) && <p className={Styles.textstyle}>{escrowCalcErrors}</p>;
  };

  const helperText = () => {
    const helperText = effdatestr === '' ? 'Please enter an effective date.' : '';
    return !isStrEmptyOrNull(helperText) && <p className={Styles.textstyle}>{helperText}</p>;
  };

  const errorMessage = useMemo(() => {
    return ''; // SQC-640 - for bankruptcy loans, escrow analysis may need to run for prior or current month
    // const earliest = earliestPossibleEffectiveDate();
    // const earliestStr = earliest.getMonth() + 1 + ' - ' + earliest.getFullYear();
    // switch (effectiveDateError) {
    //   case 'disablePast':
    //   case 'maxDate':
    //   case 'minDate':
    //   case 'invalidDate': {
    //     return 'Please select a month and year on or after ' + earliestStr;
    //   }
    //   default: {
    //     return '';
    //   }
    // }
  }, [effectiveDateError]);

  const arrayShortageMonths = useMemo(() => [...Array(49).keys()].map((i) => i + 12), []);

  const CushionMonths = () => {
    return (
      <Box sx={{ width: '100%', paddingLeft: '20px', paddingBottom: '20px' }}>
        <Autocomplete
          fullWidth
          value={escrowCushionMonths}
          onChange={(e: any, newValue: number | null) => {
            if (newValue !== null) {
              setEscrowAnalysisCalculated(false);
              setEscrowCushionMonths(newValue);
            }
          }}
          options={[0, 1, 2]}
          getOptionLabel={(option) => option.toString()}
          renderInput={(params) => (
            <TextField {...params} value={escrowCushionMonths} label={'Cushion Months'} size='small' />
          )}
        />
      </Box>
    );
  };

  const ShortageMonths = () => {
    return (
      <Box sx={{ width: '100%', paddingLeft: '20px', paddingBottom: '20px' }}>
        <Autocomplete
          fullWidth
          value={shortageMonths}
          onChange={(e: any, newValue: number | null) => {
            if (newValue !== null) {
              setEscrowAnalysisCalculated(false);
              setShortageMonths(newValue);
            }
          }}
          options={arrayShortageMonths}
          getOptionLabel={(option) => option.toString()}
          renderInput={(params) => (
            <TextField {...params} value={shortageMonths} label={'Shortage Months'} size='small' />
          )}
        />
      </Box>
    );
  };

  const EffectiveDateInputDatefield = () => {
    return (
      <Box sx={{ width: '100%', paddingLeft: '20px', paddingBottom: '20px', paddingTop: '20px' }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateField
            fullWidth
            disablePast={true}
            label='Effective Date'
            value={effectiveDate}
            format='MM-YYYY'
            onError={(newError) => setEffectiveDateError(newError)}
            slotProps={{
              textField: {
                helperText: errorMessage,
              },
            }}
            onChange={(val) => {
              setEscrowAnalysisCalculated(false);
              setEffectiveDate(val);
            }}
            onBlur={(e) => {
              if (!e.target.value.match('^[0-9]{2}-[0-9]{4}')) {
                setEffectiveDate(null);
              }
            }}
            className={Styles.emailAddr}
            size='small'
          />
        </LocalizationProvider>
      </Box>
    );
  };

  const calculateButton = () => {
    const disabled =
      effdatestr === '' ||
      errorMessage !== '' ||
      applyingAEA ||
      escrowCalcLoading ||
      applyAEAClicked ||
      applyAEAResultRetrieved;
    return (
      <div className={Styles.buttonstyle}>
        <Button
          fullWidth
          className={
            disabled
              ? ''
              : escrowAnalysisCalculated && !isObjEmptyOrNull(escrowAnalysisCalculation)
                ? Styles.whitebutton
                : Styles.bluebutton
          }
          variant='outlined'
          size='large'
          disabled={disabled}
          onClick={handleCalculate}
        >
          {'Calculate Analysis'}
        </Button>
      </div>
    );
  };

  return (
    <div className={Styles.grid}>
      <div className={Styles.smallcolumn}>
        {EffectiveDateInputDatefield()}
        {CushionMonths()}
        {ShortageMonths()}
        {calculateButton()}
        {escrowCalcErrorsText()}
        {helperText()}
        {showEscrowNumbersTable && <EscrowNumbersTable {...getEscrowAnalysisNumbers()}> </EscrowNumbersTable>}
      </div>

      {showEscrowMonthsTable && <EscrowMonthsTable {...escrowAnalysisCalculation?.trialBalances}></EscrowMonthsTable>}
      {(escrowCalcLoading || loadingDefaultValues) && <LoadingModal open={true}></LoadingModal>}
    </div>
  );
};

export default CalculateAEAView;
